import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../hooks/AuthProvider";
import CallRecordingPlayer from "../CallRecordingPlayer";
import { ColorsConstants, grayFontColor } from "../../cssUtils/constants";
import { formatWithTzLabel } from "../../utils/dateUtils";
import { VOICE_CALL_EVENTS_ENDPOINT } from "../../config/urls";
import {
  getAgentLabel,
  getStatusLabel,
  getStatusChipSx,
  getEventLabel,
  formatDuration,
} from "./voiceLogConstants";

const FULL_DATE_FORMAT = "MMMM D, YYYY, h:mm:ss A";

const secondsBetween = (from, to) =>
  from && to ? Math.max(0, (new Date(to) - new Date(from)) / 1000) : null;

const Fact = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: grayFontColor }}>{label}</Typography>
    <Typography sx={{ fontSize: 12, wordBreak: "break-word" }}>{value}</Typography>
  </Box>
);

const Section = ({ title, children }) => (
  <Box sx={{ mt: 2.5, pt: 2.5, borderTop: `1px solid ${ColorsConstants.secondaryGray}` }}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1.5 }}>{title}</Typography>
    {children}
  </Box>
);

const EventRow = ({ event, offsetSeconds }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ py: 0.5, pl: 1.75, borderLeft: `2px solid ${ColorsConstants.secondaryGray}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 34 }}>
        <Chip
          label={getEventLabel(event.event_type)}
          size="small"
          sx={{ backgroundColor: ColorsConstants.primaryBlue60, color: ColorsConstants.primaryBlue100 }}
        />
        {offsetSeconds !== null && (
          <Typography sx={{ fontSize: 11, color: grayFontColor }}>
            +{offsetSeconds.toFixed(1)}s
          </Typography>
        )}
        <Button
          size="small"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          sx={{ ml: "auto", fontSize: 11 }}
        >
          {open ? "Hide payload" : "Show payload"}
        </Button>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          component="pre"
          sx={{
            m: 0,
            my: 0.5,
            p: 1.25,
            maxHeight: 220,
            overflow: "auto",
            borderRadius: 1,
            fontSize: 11,
            backgroundColor: ColorsConstants.primaryBlue60,
          }}
        >
          {JSON.stringify(event.payload, null, 2)}
        </Box>
      </Collapse>
    </Box>
  );
};

export default function CallDetailDrawer({ call, onClose }) {
  const { getValidToken } = useAuth();
  const [events, setEvents] = useState([]);
  const [eventsState, setEventsState] = useState("idle"); // idle | loading | error | done
  const [activeCallId, setActiveCallId] = useState(null);

  const callId = call?.id;

  useEffect(() => {
    if (callId === undefined) return undefined;
    let cancelled = false;
    setEvents([]);
    setEventsState("loading");
    setActiveCallId(null);

    (async () => {
      try {
        const response = await fetch(VOICE_CALL_EVENTS_ENDPOINT(callId), {
          headers: {
            Authorization: `Bearer ${await getValidToken()}`,
            "X-Tenant-Id": localStorage.getItem("tenant"),
          },
        });
        if (!response.ok) throw new Error("Failed to fetch call events");
        const json = await response.json();
        if (cancelled) return;
        setEvents(Array.isArray(json) ? json : json.results || []);
        setEventsState("done");
      } catch (error) {
        if (!cancelled) setEventsState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId]);

  const answered = Boolean(call?.answered_at);
  const rang = call ? secondsBetween(call.started_at, call.answered_at) : null;

  const facts = call
    ? [
        ["To", call.waba_number || "-"],
        ["Agent", getAgentLabel(call.response_mode)],
        ["Rang for", answered && rang !== null ? formatDuration(rang) : "Not answered"],
        ["Duration", formatDuration(call.duration_seconds)],
        ["Answered at", call.answered_at ? formatWithTzLabel(call.answered_at, FULL_DATE_FORMAT) : "-"],
        ["Ended at", call.ended_at ? formatWithTzLabel(call.ended_at, FULL_DATE_FORMAT) : "-"],
        ["Ended because", call.hangup_cause || "-"],
        ["Template sent", call.missed_call_template_name || "None"],
        ["Subscriber", call.subscriber_code || "-"],
        ["Call ID", call.channel_id || "-"],
      ]
    : [];

  return (
    <Drawer
      anchor="right"
      open={Boolean(call)}
      onClose={onClose}
      PaperProps={{ sx: { width: "min(460px, 100vw)", p: 2.5 } }}
    >
      {call && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 17, fontWeight: 600 }}>{call.caller || "-"}</Typography>
              <Typography sx={{ fontSize: 11, color: grayFontColor }}>
                {call.started_at ? formatWithTzLabel(call.started_at, FULL_DATE_FORMAT) : "-"}
              </Typography>
            </Box>
            <Chip
              label={getStatusLabel(call.status)}
              size="small"
              sx={{ fontWeight: 500, ...getStatusChipSx(call.status) }}
            />
            <IconButton onClick={onClose} aria-label="Close details">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.75 }}>
            {facts.map(([label, value]) => (
              <Fact key={label} label={label} value={value} />
            ))}
          </Box>

          <Section title="Recording">
            {call.has_recording ? (
              <CallRecordingPlayer
                callId={call.id}
                durationSeconds={call.duration_seconds}
                activeCallId={activeCallId}
                onPlay={setActiveCallId}
              />
            ) : (
              <Typography sx={{ fontSize: 11, color: grayFontColor }}>
                No recording for this call.
              </Typography>
            )}
          </Section>

          <Section title="Call events">
            {eventsState === "loading" && <CircularProgress size={20} />}
            {eventsState === "error" && (
              <Typography sx={{ fontSize: 11, color: ColorsConstants.error }}>
                Could not load call events.
              </Typography>
            )}
            {eventsState === "done" && events.length === 0 && (
              <Typography sx={{ fontSize: 11, color: grayFontColor }}>
                No events recorded for this call.
              </Typography>
            )}
            {events.map((event) => (
              <EventRow
                key={event.id ?? `${event.event_type}-${event.created_at}`}
                event={event}
                offsetSeconds={secondsBetween(call.started_at, event.created_at)}
              />
            ))}
          </Section>
        </>
      )}
    </Drawer>
  );
}
